import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { GoogleReCaptchaProvider, GoogleReCaptcha } from 'react-google-recaptcha-v3';

// '../../' is the path to src/
import config from '../../config';
import handleResponse from '../../Components/FormComponent/FormComponentHelper';

export function RegistrationPage() {
    const navigate = useNavigate();
    const [input, setInput] = useState({
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        token: ''
    });
    const [error, setError] = useState({});
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [recaptchaReady, setRecaptchaReady] = useState(false);

    function handleChange(e) {
        const { name, value } = e.target;
        const updatedInput = {
            ...input,
            [name]: value
        };
        setInput(updatedInput);
        validateInput(e);

        // If password field changes and confirmPassword has a value, re-validate confirmPassword
        if (name === 'password' && input.confirmPassword) {
            setError(prev => {
                const stateObj = { ...prev };
                delete stateObj.confirmPassword;
                // Pass the updated input with new password value for validation
                const confirmPasswordValidation = confirmPasswordMatchValidation('confirmPassword', input.confirmPassword, updatedInput);
                if (confirmPasswordValidation) {
                    stateObj.confirmPassword = confirmPasswordValidation;
                }
                return stateObj;
            });
        }
    }

    const validateInput = e => {
        let { name, value } = e.target;
        setError(prev => {
            const stateObj = { ...prev, [name]: "" };
            delete stateObj[name];
            return { ...stateObj, ..._validateInput(name, value) }
        });
    }

    function handleSubmit(e) {
        e.preventDefault();

        if (!recaptchaReady) {
            setErrorMessage('Please wait for reCAPTCHA to load before submitting.');
            return false;
        }

        if (!input.token) {
            setErrorMessage('reCAPTCHA verification is required.');
            return false;
        }

        if (!_isFormValid()) return;

        setLoading(true);
        setErrorMessage('');

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input)
        };

        fetch(`${config.apiUrl}/api/users/register`, requestOptions)
            .then((response) => {
                return handleResponse(response)
            })
            .then(
                (response) => {
                    setLoading(false);
                    toast.success('You have been successfully registered');
                    navigate('/profile');
                },
                (error) => {
                    setErrorMessage(error);
                    setLoading(false);
                }
            );
    }

    function _isFormValid() {
        let combinedState = {};

        for (let key in input) {
            if (key !== 'token') { // Skip token validation
                combinedState = { ...combinedState, ..._validateInput(key, input[key]) };
            }
        }

        setError(prev => {
            return { ...prev, ...combinedState }
        });

        return Object.keys(combinedState).length === 0;
    }

    function _validateInput(name, value) {
        const stateObj = {};

        switch (name) {
            case "firstName":
                if (!value) {
                    stateObj[name] = "Please enter your first name.";
                }
                break;
            case "lastName":
                if (!value) {
                    stateObj[name] = "Please enter your last name.";
                }
                break;
            case "email":
                if (!value) {
                    stateObj[name] = "Please enter your email address.";
                } else if (!/\S+@\S+\.\S+/.test(value)) {
                    stateObj[name] = "Please enter a valid email address.";
                }
                break;
            case "username":
                if (!value) {
                    stateObj[name] = "Please enter a username.";
                } else {
                    const validation = usernameValidation(name, value, input);
                    if (validation) {
                        stateObj[name] = validation;
                    }
                }
                break;
            case "password":
                if (!value) {
                    stateObj[name] = "Please enter a password.";
                } else {
                    const validation = strongPasswordValidation(name, value, input);
                    if (validation) {
                        stateObj[name] = validation;
                    }
                }
                break;
            case "confirmPassword":
                if (!value) {
                    stateObj[name] = "Please confirm your password.";
                } else {
                    const validation = confirmPasswordMatchValidation(name, value, input);
                    if (validation) {
                        stateObj[name] = validation;
                    }
                }
                break;
            default:
                break;
        }
        return stateObj;
    }

    function strongPasswordValidation(name, value, input) {
        if (!value) return;

        const minLength = 8;
        const hasUpperCase = /[A-Z]/.test(value);
        const hasLowerCase = /[a-z]/.test(value);
        const hasNumbers = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>~]/.test(value);

        if (value.length < minLength) {
            return `Password must be at least ${minLength} characters long.`;
        }
        if (!hasUpperCase) {
            return 'Password must contain at least one uppercase letter.';
        }
        if (!hasLowerCase) {
            return 'Password must contain at least one lowercase letter.';
        }
        if (!hasNumbers) {
            return 'Password must contain at least one number.';
        }
        if (!hasSpecialChar) {
            return 'Password must contain at least one special character.';
        }
    }

    function confirmPasswordMatchValidation(name, value, input) {
        if(input.password !== value) {
            return 'Password and Confirm Password do not match.';
        }
    }

    function usernameValidation(name, value, input) {
        if (!value) return;

        const minLength = 3;
        const maxLength = 30;

        // Username pattern: alphanumeric characters, dots, hyphens, underscores
        // Must start and end with alphanumeric character
        const usernamePattern = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?$/;

        if (value.length < minLength) {
            return `Username must be at least ${minLength} characters long.`;
        }
        if (value.length > maxLength) {
            return `Username must be no more than ${maxLength} characters long.`;
        }
        if (!usernamePattern.test(value)) {
            return 'Username can only contain letters, numbers, dots, hyphens, and underscores. Must start and end with a letter or number.';
        }

        // Check for consecutive special characters
        if (/[._-]{2,}/.test(value)) {
            return 'Username cannot contain consecutive dots, hyphens, or underscores.';
        }

        // Reserved usernames (common system/admin usernames)
        const reservedUsernames = [
            'admin', 'administrator', 'root', 'system', 'api', 'www', 'mail', 'email',
            'support', 'help', 'info', 'contact', 'service', 'user', 'guest', 'anonymous',
            'null', 'undefined', 'test', 'demo', 'sample', 'example', 'default'
        ];

        if (reservedUsernames.includes(value.toLowerCase())) {
            return 'This username is reserved and cannot be used.';
        }
    }

    const onVerify = useCallback((token) => {        
        setInput(prev => ({
            ...prev,
            token: token
        }));
        setRecaptchaReady(true);
    }, []);

    return (
        <div className="min-vh-100 d-flex align-items-center" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-8 col-lg-6">
                        <div className="modern-card text-center mb-4">
                            <div className="card-body p-5">
                                <div className="mb-4">
                                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                         style={{width: '4rem', height: '4rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                                        <i className="bi bi-person-plus-fill text-white fs-3"></i>
                                    </div>
                                    <h2 className="fw-bold mb-2" style={{color: '#1e293b'}}>Create Account</h2>
                                    <p className="text-muted mb-0">Join us and start managing your content</p>
                                </div>

                                {errorMessage && (
                                    <div className="alert alert-danger rounded-3 mb-4" role="alert">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        {errorMessage}
                                    </div>
                                )}

                                <form name="form" onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className={'mb-3' + (error.firstName ? ' has-error' : '')}>
                                                <div className="form-floating">
                                                    <input
                                                        type="text"
                                                        className={'form-control ' + (error.firstName ? 'is-invalid' : '')}
                                                        id="firstName"
                                                        name="firstName"
                                                        value={input.firstName}
                                                        onChange={handleChange}
                                                        onBlur={validateInput}
                                                        autoComplete="given-name"
                                                        placeholder="First Name"
                                                    />
                                                    <label htmlFor="firstName">First Name</label>
                                                    {error.firstName && <div className='invalid-feedback'>{error.firstName}</div>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className={'mb-3' + (error.lastName ? ' has-error' : '')}>
                                                <div className="form-floating">
                                                    <input
                                                        type="text"
                                                        className={'form-control ' + (error.lastName ? 'is-invalid' : '')}
                                                        id="lastName"
                                                        name="lastName"
                                                        value={input.lastName}
                                                        onChange={handleChange}
                                                        onBlur={validateInput}
                                                        autoComplete="family-name"
                                                        placeholder="Last Name"
                                                    />
                                                    <label htmlFor="lastName">Last Name</label>
                                                    {error.lastName && <div className='invalid-feedback'>{error.lastName}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={'mb-3' + (error.email ? ' has-error' : '')}>
                                        <div className="form-floating">
                                            <input
                                                type="email"
                                                className={'form-control ' + (error.email ? 'is-invalid' : '')}
                                                id="email"
                                                name="email"
                                                value={input.email}
                                                onChange={handleChange}
                                                onBlur={validateInput}
                                                autoComplete="email"
                                                placeholder="Email Address"
                                            />
                                            <label htmlFor="email">Email Address</label>
                                            {error.email && <div className='invalid-feedback'>{error.email}</div>}
                                        </div>
                                    </div>

                                    <div className={'mb-3' + (error.username ? ' has-error' : '')}>
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className={'form-control ' + (error.username ? 'is-invalid' : '')}
                                                id="username"
                                                name="username"
                                                value={input.username}
                                                onChange={handleChange}
                                                onBlur={validateInput}
                                                autoComplete="username"
                                                placeholder="Username"
                                            />
                                            <label htmlFor="username">Username</label>
                                            {error.username && <div className='invalid-feedback'>{error.username}</div>}
                                        </div>
                                        <div className="form-text mt-1 text-muted small">
                                            <i className="bi bi-info-circle me-1"></i>
                                            3-30 characters, letters/numbers only, can include dots, hyphens, underscores
                                        </div>
                                    </div>

                                    <div className={'mb-3' + (error.password ? ' has-error' : '')}>
                                        <div className="form-floating">
                                            <input
                                                type="password"
                                                className={'form-control ' + (error.password ? 'is-invalid' : '')}
                                                id="password"
                                                name="password"
                                                value={input.password}
                                                onChange={handleChange}
                                                onBlur={validateInput}
                                                autoComplete="new-password"
                                                placeholder="Password"
                                            />
                                            <label htmlFor="password">Password</label>
                                            {error.password && <div className='invalid-feedback'>{error.password}</div>}
                                        </div>
                                        <div className="form-text mt-1 text-muted small">
                                            <i className="bi bi-info-circle me-1"></i>
                                            Must be 8+ characters with uppercase, lowercase, number, and special character
                                        </div>
                                    </div>

                                    <div className={'mb-3' + (error.confirmPassword ? ' has-error' : '')}>
                                        <div className="form-floating">
                                            <input
                                                type="password"
                                                className={'form-control ' + (error.confirmPassword ? 'is-invalid' : '')}
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                value={input.confirmPassword}
                                                onChange={handleChange}
                                                onBlur={validateInput}
                                                autoComplete="new-password"
                                                placeholder="Confirm Password"
                                            />
                                            <label htmlFor="confirmPassword">Confirm Password</label>
                                            {error.confirmPassword && <div className='invalid-feedback'>{error.confirmPassword}</div>}
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <div id="recaptcha"></div>
                                        {!recaptchaReady && (
                                            <div className="text-center text-muted">
                                                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                                                <small>Loading reCAPTCHA...</small>
                                            </div>
                                        )}
                                        <GoogleReCaptchaProvider reCaptchaKey={config.recaptchaKey} useEnterprise="true"
                                            container={{ element: 'recaptcha', parameters: { theme: 'dark' } }}
                                        >
                                            <GoogleReCaptcha onVerify={onVerify} />
                                        </GoogleReCaptchaProvider>
                                    </div>

                                    <div className="d-grid gap-2 mb-4">
                                        <button
                                            type="submit"
                                            className="btn btn-modern btn-lg"
                                            disabled={!recaptchaReady || loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Creating Account...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-person-plus me-2"></i>
                                                    Create Account
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    <div className="text-center">
                                        <span className="text-muted me-2">Already have an account?</span>
                                        <Link to="/login" className="btn btn-outline-modern">
                                            <i className="bi bi-box-arrow-in-right me-2"></i>
                                            Sign In
                                        </Link>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="text-center">
                            <p className="text-white-50 small mb-0">
                                <i className="bi bi-shield-check me-1"></i>
                                By creating an account, you agree to our Terms of Service and Privacy Policy
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}